import type {
  CurrentMlxUser,
  WorkspaceOrganizationMember,
  WorkspaceSession,
} from "@/lib/mlx-workspace-client";

export type WorkspaceSessionOwnerDisplay = {
  label: string;
  title: string;
  ownerId: string | null;
  imageUrl: string | null;
  initials: string;
};

export type WorkspaceActorIdentity = {
  userId?: string;
  verifiedSubjectId?: string;
  pluginSubjectId?: string;
};

export function organizationMembersByUserId(
  members: WorkspaceOrganizationMember[] | undefined,
): Map<string, WorkspaceOrganizationMember> {
  const map = new Map<string, WorkspaceOrganizationMember>();
  for (const member of members ?? []) {
    if (member.id.trim()) map.set(member.id, member);
    if (member.userId.trim()) map.set(member.userId, member);
    if (member.externalUserId?.trim()) map.set(member.externalUserId, member);
    if (member.email?.trim())
      map.set(member.email.trim().toLocaleLowerCase(), member);
  }
  return map;
}

export function organizationMemberForActor(
  actorId: string | null | undefined,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): WorkspaceOrganizationMember | null {
  const id = actorId?.trim();
  if (!id) return null;
  return (
    membersByUserId.get(id) ??
    membersByUserId.get(id.toLocaleLowerCase()) ??
    null
  );
}

export function currentMlxUserMatchesActor(
  currentUser: CurrentMlxUser | null,
  actorId: string | null | undefined,
): boolean {
  const id = actorId?.trim();
  if (!currentUser || !id) return false;
  return (
    currentUser.id === id ||
    Boolean(
      currentUser.email?.trim() &&
      currentUser.email.trim().toLocaleLowerCase() === id.toLocaleLowerCase(),
    )
  );
}

export function workspaceSessionOwnerDisplay(
  session: WorkspaceSession,
  currentUser: CurrentMlxUser | null,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): WorkspaceSessionOwnerDisplay {
  return workspaceActorDisplay(session, currentUser, membersByUserId);
}

export function workspaceActorDisplay(
  actor: WorkspaceActorIdentity,
  currentUser: CurrentMlxUser | null,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): WorkspaceSessionOwnerDisplay {
  const ownerIds = workspaceActorIds(actor);
  const ownerId = ownerIds[0] ?? null;
  if (!ownerId) {
    return {
      label: "Unassigned",
      title: "Unassigned",
      ownerId: null,
      imageUrl: null,
      initials: "T",
    };
  }
  if (currentUser && ownerIds.includes(currentUser.id)) {
    const label =
      currentUser.name?.trim() || currentUser.email?.trim() || "You";
    return ownerDisplay(
      label,
      currentUser.id,
      currentUser.imageUrl || membersByUserId.get(currentUser.id)?.imageUrl,
    );
  }
  for (const id of ownerIds) {
    const member = organizationMemberForActor(id, membersByUserId);
    if (!member) continue;
    const label = member.name?.trim() || member.email?.trim() || compactId(id);
    return ownerDisplay(label, id, member.imageUrl);
  }
  const label = compactId(ownerId);
  return {
    label,
    title: ownerId,
    ownerId,
    imageUrl: null,
    initials: ownerInitials(label),
  };
}

export function workspaceUserDisplay(
  userId: string | undefined,
  currentUser: CurrentMlxUser | null,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): WorkspaceSessionOwnerDisplay | null {
  if (!userId?.trim()) return null;
  const id = userId.trim();
  if (currentUser?.id === id) {
    const label =
      currentUser.name?.trim() || currentUser.email?.trim() || "You";
    return ownerDisplay(
      label,
      id,
      currentUser.imageUrl || membersByUserId.get(id)?.imageUrl,
    );
  }
  const member = organizationMemberForActor(id, membersByUserId);
  if (member) {
    const label = member.name?.trim() || member.email?.trim() || compactId(id);
    return ownerDisplay(label, id, member.imageUrl);
  }
  const label = compactId(id);
  return {
    label,
    title: id,
    ownerId: id,
    imageUrl: null,
    initials: ownerInitials(label),
  };
}

export function workspaceSessionOwnedByCurrentUser(
  session: WorkspaceSession,
  currentUserId: string | null,
): boolean {
  return Boolean(
    currentUserId && workspaceActorIds(session).includes(currentUserId),
  );
}

function workspaceActorIds(actor: WorkspaceActorIdentity): string[] {
  return [actor.pluginSubjectId, actor.verifiedSubjectId, actor.userId]
    .flatMap((value) => (value?.trim() ? [value.trim()] : []))
    .filter((value, index, values) => values.indexOf(value) === index);
}

function ownerDisplay(
  label: string,
  ownerId: string,
  imageUrl?: string,
): WorkspaceSessionOwnerDisplay {
  return {
    label,
    // A person reads by name; their account ID is not something people use.
    title: label,
    ownerId,
    imageUrl: imageUrl?.trim() || null,
    initials: ownerInitials(label),
  };
}

function ownerInitials(label: string): string {
  const parts = label
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
  return `${parts[0]?.[0] ?? ""}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
}

function compactId(value: string): string {
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}
